import classNames from "classnames";
import styles from "./AscSkillsRadarChart.module.scss";
import RadarChart, { ChartData } from "react-svg-radar-chart";
import { SkillHexagonIllustration } from "../AscIllustrations";
import { useMemo } from "react";

export interface AscSkillsRadarChartProps {
  className?: string;
  data: {
    label: string;
    value: number;
  }[];
}

function gridLines(count = 5) {
  return new Array(count).fill(0).map((_, index) => {
    const data: ChartData["data"] = {};
    for (let i = 1; i <= count; i++) {
      data[`${i}`] = 1 - index / count;
    }
    return {
      data,
      meta: { class: styles.gridLines },
    } as unknown as ChartData;
  });
}

const INITIAL_DATA = [
  {
    label: "label",
    value: 0,
  },
];

export default function AscSkillsRadarChart({
  className,
  data = INITIAL_DATA,
}: AscSkillsRadarChartProps) {
  const maxValue = Math.max(...(data?.map(({ value }) => value) || []));

  const _data = useMemo(
    () => [
      ...gridLines(data?.length),
      data?.reduce(
        (acc, { value }, index) => {
          acc.data[`${index + 1}`] = value / maxValue;
          acc.meta = { class: styles.dataLines };
          return acc;
        },
        { data: {} as ChartData["data"], meta: {} }
      ) as ChartData,
    ],
    [data, maxValue]
  );

  const captions = useMemo(
    () =>
      data?.reduce(
        (acc, { value }, index) => ({
          ...acc,
          [`${index + 1}`]: `${value}`,
        }),
        {}
      ),
    [data]
  );

  return (
    <div
      data-testid="asc-skills-radar-chart"
      className={classNames(styles.ground, className)}
    >
      <div className={styles.chartWrapper}>
        <RadarChart
          captions={captions || {}}
          data={_data}
          size={300}
          options={{
            scales: 0,
            dots: true,
          }}
        />
      </div>

      <div className={styles.legend}>
        {data?.map(({ label, value }, index) => (
          <div key={index} className={classNames(styles.legendItem)}>
            <div
              className={classNames(
                styles.legendItemValue,
                styles[`_${index + 1}th`]
              )}
            >
              <span
                className={styles.valueText}
                data-testid={`asc-skills-radar-chart-value-${index + 1}`}
              >
                {value}
              </span>
              <SkillHexagonIllustration className={styles.icon} />
            </div>
            <div
              className={styles.legendItemLabel}
              data-testid={`asc-skills-radar-chart-label-${index + 1}`}
            >
              {label}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
